import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { FileSignature, Inbox, Receipt } from 'lucide-react';
import { LanguageSwitcher } from './LanguageSwitcher.js';
import { WorkiLogo } from './WorkiLogo.js';
import { cn } from '../lib/cn.js';

/**
 * The frame both auth screens sit in: the pitch beside the form.
 *
 * AUTH-011 — this is also the landing page. A visitor who types the address
 * arrives here, so the screen has to say what Worki is before it asks for an
 * email; the panel on the wide side does that, and folds into a single line on
 * a phone, where the form is the whole point of the visit.
 *
 * It is shared and not copied because sign-in and sign-up are the same screen
 * with different fields, and the one place a visitor forms an impression of the
 * app should not be able to drift apart between them.
 */

const PITCH = [
  { icon: Inbox, title: 'auth.pitchLeads', body: 'auth.pitchLeadsBody' },
  { icon: FileSignature, title: 'auth.pitchQuotes', body: 'auth.pitchQuotesBody' },
  { icon: Receipt, title: 'auth.pitchInvoices', body: 'auth.pitchInvoicesBody' },
] as const;

/**
 * 1.64 — these two screens' own type scale, for eyes that are not at their best.
 *
 * What `Field`, `Input` and `ErrorNotice` give — a 12px label, an 11px hint, a
 * 14px box 40px high — is right for a dense screen inside the app, and stays
 * as it is for every one of them. So it is raised from above, on the region
 * the children sit in: a visitor opening an account or signing in reads 14px
 * and types in 16px, and nothing else moves. The footer and its language
 * picker are outside the region, and the dropdown's list is portalled out of
 * it, so both keep their size.
 *
 * The box after dark is the field the landing card already has: a boundary
 * lifted out of `--border` (it was 1.4:1 against the card; WCAG 1.4.11 asks a
 * control's boundary for 3:1), and the dropdown, which has no boundary at all
 * in the shared code, is given the same one.
 */
const READABLE = [
  // The label above a field, and the terms line beside the checkbox.
  '[&_label]:text-sm',
  // A hint or an error under a field, the error banner, and "or by email".
  '[&_span.block]:text-sm',
  '[&_[role=alert]]:text-sm',
  '[&_[aria-hidden=true]>span]:text-sm',
  // What a visitor types or picks into: 48px high and 16px, which is also the
  // size under which a phone browser zooms the page when the box is focused.
  // Not the terms checkbox, which is an <input> as well.
  '[&_:is(input:not([type=checkbox]),button[role=combobox])]:h-12',
  '[&_:is(input:not([type=checkbox]),button[role=combobox])]:text-base',
  'dark:[&_:is(input:not([type=checkbox]),button[role=combobox])]:border',
  'dark:[&_:is(input:not([type=checkbox]),button[role=combobox])]:border-[color:color-mix(in_srgb,hsl(var(--border)),hsl(var(--foreground))_34%)]',
];

function Wordmark({ size = 'sm' }: { size?: 'sm' | 'lg' }) {
  return (
    <WorkiLogo
      className={
        size === 'lg'
          ? 'h-9 w-auto text-primary-foreground'
          : 'h-10 w-auto text-primary'
      }
      aria-hidden
    />
  );
}

export function AuthShell({
  title,
  subtitle,
  onSubmit,
  children,
  footer,
  wide = false,
}: {
  title: string;
  subtitle: string;
  onSubmit: (event: React.FormEvent) => void;
  children: ReactNode;
  footer: ReactNode;
  /** Sign-up asks for six things and pairs them; sign-in asks for one. */
  wide?: boolean;
}) {
  const { t } = useTranslation();

  return (
    <div className="min-h-dvh lg:grid lg:grid-cols-[1fr_minmax(0,35rem)] pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]">
      {/* The pitch. Hidden below `lg` rather than stacked above the form: on a
          phone it would push the email field under the fold, and the visitor
          who opened the app to sign in came for the field. */}
      <aside className="hidden lg:flex flex-col justify-between gap-10 bg-gradient-to-bl from-primary via-primary to-primary/80 text-primary-foreground p-10 xl:p-14">
        <div className="flex items-center">
          <Wordmark size="lg" />
        </div>

        <div className="max-w-md space-y-8">
          <div className="space-y-3">
            <h2 className="text-3xl xl:text-4xl font-bold leading-tight text-balance">{t('auth.pitchTitle')}</h2>
            <p className="text-sm xl:text-base text-primary-foreground/80 leading-relaxed">{t('auth.pitchBody')}</p>
          </div>

          <ul className="space-y-5">
            {PITCH.map(({ icon: Glyph, title: heading, body }) => (
              <li key={heading} className="flex items-start gap-3">
                <span className="h-9 w-9 shrink-0 rounded-xl bg-primary-foreground/15 grid place-items-center">
                  <Glyph className="h-4 w-4" aria-hidden />
                </span>
                <div className="space-y-0.5">
                  <p className="text-sm font-semibold">{t(heading)}</p>
                  <p className="text-sm text-primary-foreground/75 leading-relaxed">{t(body)}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <p className="text-xs text-primary-foreground/60">{t('auth.pitchFooter')}</p>
      </aside>

      <main className="flex items-center justify-center p-4 sm:p-6">
        <form
          onSubmit={onSubmit}
          noValidate
          className={cn(
            'w-full rounded-2xl border border-border/60 bg-card p-6 space-y-4 shadow-lg animate-fade-up',
            // After dark the card is lifted off the page the way the landing's
            // sign-in card is (landing.css, LAND-004): a boundary and a surface
            // mixed up from the tokens, and a shadow stated in ink —
            // `--foreground` is near-white there, so one tinted with it is a halo.
            'dark:border-[color:color-mix(in_srgb,hsl(var(--border)),hsl(var(--foreground))_14%)]',
            'dark:bg-[color:color-mix(in_srgb,hsl(var(--card)),hsl(var(--foreground))_5%)]',
            'dark:shadow-[0_18px_46px_hsl(266_55%_2%/0.66)]',
            wide ? 'max-w-lg' : 'max-w-md',
          )}
        >
          <div className="space-y-2 text-center">
            <span className="mx-auto block w-fit lg:hidden">
              <Wordmark />
            </span>
            <h1 className="text-2xl font-bold text-foreground">{title}</h1>
            <p className="text-base text-muted-foreground">{subtitle}</p>
          </div>

          <div className={cn('space-y-4', READABLE)}>{children}</div>

          <div className="flex items-center justify-between gap-2 pt-1 flex-wrap">
            <span className="text-sm text-muted-foreground">{footer}</span>
            <LanguageSwitcher />
          </div>
        </form>
      </main>
    </div>
  );
}
